import { computed, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProcessingVideo, ProcessingVideoStatus } from '@/services/videos/types'

/** Matches `video_id` from AI status polling (`ai-${id}` in useVideoPolling). */
const AI_PROCESSING_VIDEO_ID_PREFIX = 'ai-'

export function isAiProcessingVideoId(videoId: string): boolean {
  return videoId.startsWith(AI_PROCESSING_VIDEO_ID_PREFIX)
}

export function useProcessingPanelItem(video: Ref<ProcessingVideo>) {
  const { t } = useI18n()

  const isAiVideo = computed(() => isAiProcessingVideoId(video.value.video_id))

  const isComplete = computed(() => video.value.status === ProcessingVideoStatus.Active || video.value.status === ProcessingVideoStatus.Completed)
  const isFailed = computed(() => video.value.status === ProcessingVideoStatus.Failed || !video.value.status)
  const isPending = computed(() => video.value.status === ProcessingVideoStatus.Pending)
  const isProcessing = computed(() => video.value.status === ProcessingVideoStatus.Transcoding || video.value.status === ProcessingVideoStatus.Fetching || video.value.status === ProcessingVideoStatus.Processing)

  const statusText = computed(() => {
    if (isAiVideo.value && (isProcessing.value || isPending.value)) return t('generate-ai-video-generating')
    if (isPending.value) return t('in-queue')
    if (isComplete.value) return t('video-upload-complete')
    if (isFailed.value) return t('video-upload-failed')
    return t('video-processing')
  })

  const statusIcon = computed(() => {
    if (isAiVideo.value && isProcessing.value) return null
    if (isProcessing.value) return 'loadingPrimaryIcon'
    if (isComplete.value) return 'checkBodyTextIcon'
    if (isFailed.value) return 'alertRedIcon'
    return null
  })

  return {
    isAiVideo,
    isProcessing,
    isComplete,
    isFailed,
    statusText,
    statusIcon
  }
}

